import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { MultiSelect } from "primereact/multiselect";
import { Button, Dialog } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { DialogProps } from "../dialog/CustomDialog";
import { ConsoleTranslationsSchema } from "../../validations/schemas";
import {
  REVALIDATE_FORM_PROPS,
  TRANSLATION_LABELTYPE_OPTIONS,
} from "../../constants/index";
import {
  LANGUAGE_OPTIONS,
} from "../../constants";
import { AppContext } from "../../context";
import { useConsoleFunctionality } from "../../hooks/useConsoleFunctionalities";

interface ConsoleTranslationCreatorProps {
  visible: boolean;
  setModalProps: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      type: "CREATE" | "UPDATE" | null;
    }>
  >;
  update: boolean;
  actionFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const CTDefaultValues = {
  key: "",
  type: "",
  functionality: [],
  translationlanguage: "",
  translationvalue: "",
};

export const ConsoleTranslationCreator: React.FC<
  ConsoleTranslationCreatorProps
> = ({
  visible,
  setModalProps,
  update,
  actionFn,
  reloadFn,
  auxModalController,
}) => {
  const {
    t,
    appState: { user, selectedConsoleLabel },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const [consoleTranslationsFunctionalities, setConsoleTranslationsFunctionalities] = useState([]);
  const functionalities = useConsoleFunctionality('DATA')['data'];

  useEffect(()=>{
   if (functionalities) {
    setConsoleTranslationsFunctionalities(functionalities.map(
      (item: { id:number, key:string, keylabel:string }) => ({
          label: t(`ConsoleTranslations.${item.key}OptionLabel`),
          value: item.key,
      })));
   }
  }, [functionalities]);

  const onHideModal = () => {
    setModalProps((prev) => ({
      ...prev,
      visible: false,
    }));
    reset(CTDefaultValues);
  };

  const onCreateTranslation = async () => {
    const formValues = getValues();
    onHideModal();
    const actionStatus = await actionFn(update ? "UPDATE" : "CREATE", {
      ...formValues,
      [update ? "updatedby" : "createdby"]: user?.name,
    });
    if (actionStatus === "SUCCESS") {
      reloadFn();
    }
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Versions.cancelModalTitle")
        : t(`Versions.${update ? "save" : "create"}ModalTitle`),
      content: isCancelButton
        ? t("Versions.cancelModalContent")
        : t(`Versions.${update ? "save" : "create"}ModalContent`),
      onConfirm: isCancelButton ? onHideModal : onCreateTranslation,
    }));
  };

  useEffect(() => {
    if (update && selectedConsoleLabel) {
      reset({
        id: selectedConsoleLabel.id,
        key: selectedConsoleLabel.key,
        type: selectedConsoleLabel.type,
        functionality: selectedConsoleLabel?.functionality,
        translationid: selectedConsoleLabel?.translationid,
        translationlanguage: selectedConsoleLabel?.translationlanguage,
        translationvalue: selectedConsoleLabel?.translationvalue,
      });
    } else {
      reset(CTDefaultValues);
    }
  }, [reset, selectedConsoleLabel, setAppState, update]);

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("ConsoleTranslations.cancelBtnLabel")}
          onClick={
            isDirty && update ? () => handleModalProps(true) : onHideModal
          }
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={
            update
              ? t("ConsoleTranslations.editBtnLabel")
              : t("ConsoleTranslations.createBtnLabel")
          }
          variant="p-button"
          onClick={() => handleModalProps()}
          disabled={update ? !(isValid && isDirty) : !isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      className="dialog-form"
      header={
        update
          ? t("ConsoleTranslations.editTranslationTitle")
          : t("ConsoleTranslations.newTranslationTitle")
      }
      visible={visible}
      onHide={onHideModal}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        {Object.keys(getValues())
          .filter((key) => !["id", "translationid"].includes(key))
          .map((key) => {
            return (
              <div key={key}>
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  rules={(ConsoleTranslationsSchema(t) as any)[key]}
                  render={({ field }) => {
                    if (key === "functionality") {
                      field.onChange = (e) => {
                        const sortedValues = e.value.sort(
                          (a: string, b: string) => a.localeCompare(b)
                        );
                        setValue(
                          "functionality",
                          sortedValues,
                          REVALIDATE_FORM_PROPS
                        );
                      };
                    }

                    const extraProps = {
                      keyfilter: (ConsoleTranslationsSchema(t) as any)[key]
                        ?.keyfilter,
                      maxLength: (ConsoleTranslationsSchema(t) as any)[key]
                        ?.maxLength?.value,
                    };
                    if (key === "translationlanguage") {
                      return (
                        <Dropdown
                          className="dropdown mb-3"
                          label={t(
                            `ConsoleTranslations.translationlanguageLabel`
                          )}
                          placeholder={t(
                            `ConsoleTranslations.translationlanguagePlaceholder`
                          )}
                          options={LANGUAGE_OPTIONS(t).sort((a, b) =>
                            a.label.localeCompare(b.label)
                          )}
                          filter={false}
                          showClear={false}
                          style={{
                            zIndex: "6000 !important",
                          }}
                          {...field}
                        />
                      );
                    } else if (key === "type") {
                      return (
                        <Dropdown
                          className="dropdown mb-3"
                          label={t(`ConsoleTranslations.typeLabel`)}
                          placeholder={t(`ConsoleTranslations.typePlaceholder`)}
                          options={TRANSLATION_LABELTYPE_OPTIONS(t).sort(
                            (a, b) => a.label.localeCompare(b.label)
                          )}
                          filter={false}
                          showClear={false}
                          style={{
                            zIndex: "6000 !important",
                          }}
                          {...field}
                        />
                      );
                    } else if (key === "functionality") {
                      return (
                        <>
                          <label htmlFor={"functionality"}>
                            {t(`ConsoleTranslations.functionalityLabel`)}
                          </label>
                          <MultiSelect
                            id={`select-functionality`}
                            display="chip"
                            placeholder={t(
                              `ConsoleTranslations.functionalityPlaceholder`
                            )}
                            className="pt-0 mb-3"
                            options={consoleTranslationsFunctionalities.sort(
                              (a:any, b:any) => a.label.localeCompare(b.label)
                            )}
                            emptyFilterMessage={t(
                              "ConsoleTranslations.noAvailableMessage"
                            )}
                            filter
                            {...field}
                          />
                        </>
                      );
                    } else {
                      return (
                        <CustomInput
                          id={`input-${key}`}
                          className={`${errors[key]?.message ? "p-invalid" : "mb-3"}`}
                          label={t(`ConsoleTranslations.${key}Label`)}
                          placeholder={t(
                            `ConsoleTranslations.${key}Placeholder`
                          )}
                          type="text"
                          autoComplete="off"
                          // keyfilter={
                          //   (WidgetCreatorSchema(t) as any)[key]?.keyfilter
                          // }
                          // maxLength={
                          //   (WidgetCreatorSchema(t) as any)[key]?.maxLength?.value
                          // }
                          {...field}
                          {...extraProps}
                        />
                      );
                    }
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name={key}
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{ marginTop: "0rem", marginBottom: "1.5rem" }}
                    >
                      {message}
                    </small>
                  )}
                />
              </div>
            );
          })}
      </div>
    </Dialog>
  );
};
